<ng-template #searchModal>
  <div class="cmdk" [class.is-searching]="searching" role="dialog" aria-label="Busca universal">
    <!-- Input -->
    <div class="cmdk-input">
      <i class="ti ti-search lead"></i>
      <input
        #searchInput
        type="text"
        placeholder="Buscar pedido, produto, SKU, nota, anúncio ou página"
        autocomplete="off"
        aria-label="Buscar"
        [(ngModel)]="query"
        (ngModelChange)="onQueryChange()"
        (keydown)="onKeydown($event)"
      />
      <span class="kbd">esc</span>
      <!-- Touch não tem esc e o backdrop fica mínimo com o dialog quase full-screen. -->
      <button type="button" class="cmdk-close d-sm-none" aria-label="Fechar busca" (click)="close()">
        <i class="ti ti-x"></i>
      </button>
    </div>

    <!-- Filtros em pílula: só com busca digitada (o estado vazio não tem o que filtrar). -->
    @if (scopes.length) {
      <div class="cmdk-scopes" role="group" aria-label="Filtrar resultados">
        @for (s of scopes; track s.id) {
          <button
            type="button"
            class="cmdk-scope"
            tabindex="-1"
            [class.active]="s.active"
            [attr.aria-pressed]="s.active"
            (click)="setScope(s.id)"
          >
            {{ s.label }} <span class="count">{{ s.count }}</span>
          </button>
        }
      </div>
    }

    <div class="cmdk-area">
      <!-- Resultados -->
      <div class="cmdk-body">
        @if (noResults) {
          <div class="cmdk-empty">
            <i class="ti ti-search-off"></i>
            <h6>Nenhum resultado para “{{ query.trim() }}”</h6>
            <p>Busque pelo número do pedido, SKU, número da nota ou nome do produto.</p>
          </div>
        } @else if (!searching) {
          <!-- Estado vazio: favoritos em pílulas, abertos recentemente em lista, ações em grade. -->
          @for (group of visibleGroups; track group.label) {
            <div class="cmdk-group-label">{{ group.label }}</div>
            @switch (group.layout) {
              @case ('chips') {
                <div class="cmdk-pills">
                  @for (item of group.items; track item.id) {
                    <button
                      type="button"
                      class="cmdk-pill"
                      [class.active]="isSelected(item)"
                      (mousemove)="hover(item)"
                      (click)="select(item)"
                    >
                      <i class="ti ti-star-filled"></i>{{ item.title }}
                    </button>
                  }
                </div>
              }
              @case ('tiles') {
                <div class="cmdk-tiles">
                  @for (item of group.items; track item.id) {
                    <button
                      type="button"
                      class="cmdk-tile"
                      [class.active]="isSelected(item)"
                      (mousemove)="hover(item)"
                      (click)="select(item)"
                    >
                      <span class="cmdk-ic" [ngClass]="iconTone(item)"><i [class]="item.icon"></i></span>
                      <span class="cmdk-main">
                        <span class="cmdk-title">{{ item.title }}</span>
                        @if (item.subtitle) {
                          <span class="cmdk-sub">{{ item.subtitle }}</span>
                        }
                      </span>
                    </button>
                  }
                </div>
              }
              @default {
                @for (item of group.items; track item.id) {
                  <ng-container *ngTemplateOutlet="row; context: { $implicit: item }"></ng-container>
                }
              }
            }
          }
          <p class="cmdk-tip">
            Busque pelo número do pedido no canal, pelo SKU, pelo número da nota ou pelo nome do produto.
          </p>
        } @else {
          @for (group of visibleGroups; track group.label) {
            <div class="cmdk-group-label">{{ group.label }}</div>
            @for (item of group.items; track item.id) {
              <ng-container *ngTemplateOutlet="row; context: { $implicit: item }"></ng-container>
            }
          }
        }
      </div>

      <!-- Prévia do item realçado (telas largas): só com o que a busca já trouxe. -->
      @if (hasPreview && current; as item) {
        <aside class="cmdk-preview" aria-label="Prévia">
          <div class="pv-head">
            <span class="cmdk-ic pv-ic" [ngClass]="iconTone(item)"><i [class]="item.icon"></i></span>
            <span class="pv-titles">
              <span class="pv-title">{{ item.title }}</span>
              @if (item.subtitle) {
                <span class="pv-sub">{{ item.subtitle }}</span>
              }
            </span>
          </div>
          @if (item.meta) {
            <span class="cmdk-chip pv-chip" [ngClass]="'chip-' + item.meta.tone">{{ item.meta.text }}</span>
          }
          <div class="pv-facts">
            @for (f of item.preview!.facts; track f.label) {
              <div class="pv-fact">
                <span class="pv-label">{{ f.label }}</span>
                <span class="pv-value">{{ f.value }}</span>
              </div>
            }
          </div>
          @if (item.preview!.list; as list) {
            <div class="pv-list">
              <span class="pv-list-title">{{ list.title }}</span>
              @for (r of list.rows; track r.label) {
                <div class="pv-list-row">
                  <span>{{ r.label }}</span>
                  <strong [class.text-danger]="r.tone === 'bad'">{{ r.value }}</strong>
                </div>
              }
            </div>
          }
          <div class="pv-actions">
            <button type="button" class="btn btn-primary" tabindex="-1" (click)="select(item)">
              {{ item.preview!.primary }}
            </button>
            @for (a of item.preview!.actions; track a.label) {
              <button type="button" class="btn btn-outline-secondary" tabindex="-1" (click)="runAction(item, a)">
                {{ copied === a.label ? 'Copiado' : a.label }}
              </button>
            }
          </div>
        </aside>
      }
    </div>

    <!-- Rodapé com atalhos -->
    <div class="cmdk-foot">
      <span class="leg"><span class="kbd">↑</span><span class="kbd">↓</span> navegar</span>
      <span class="leg"><span class="kbd">↵</span> abrir</span>
      @if (searching) {
        <span class="leg"><span class="kbd">Tab</span> trocar o filtro</span>
      }
      <span class="leg"><span class="kbd">Ctrl</span><span class="kbd">D</span> favoritar</span>
      <span class="leg"><span class="kbd">esc</span> fechar</span>
      <span class="spacer"></span>
      <span class="brand-note">Busca universal</span>
    </div>
  </div>
</ng-template>

<!-- Linha de resultado = item + estrela IRMÃ (botão dentro de botão é HTML inválido e o clique na
     estrela abriria o item). A estrela fica por cima do canto direito do item. -->
<ng-template #row let-item>
  <div class="cmdk-row" [class.is-fav]="isFavorite(item)" (mousemove)="hover(item)">
    <button type="button" class="cmdk-item" [class.active]="isSelected(item)" (click)="select(item)">
      <span class="cmdk-ic" [ngClass]="iconTone(item)"><i [class]="item.icon"></i></span>

      <span class="cmdk-main">
        <span class="cmdk-title">
          @if (parts(item.title, item.hlTitle); as p) {
            {{ p.pre }}<mark>{{ p.mark }}</mark>{{ p.post }}
          }
        </span>
        <!-- Celular: o selo desce para baixo do título (ao lado, espremia o número do pedido). -->
        @if (item.meta) {
          <span class="cmdk-chip cmdk-chip-below d-sm-none" [ngClass]="'chip-' + item.meta.tone">{{ item.meta.text }}</span>
        }
        @if (item.subtitle) {
          <span class="cmdk-sub">
            @if (parts(item.subtitle, item.hlSubtitle); as p) {
              {{ p.pre }}<mark>{{ p.mark }}</mark>{{ p.post }}
            }
          </span>
        }
      </span>

      <span class="cmdk-meta">
        @if (item.meta) {
          <span class="cmdk-chip d-none d-sm-inline" [ngClass]="'chip-' + item.meta.tone">{{ item.meta.text }}</span>
        }
        @if (item.hint) {
          <span class="cmdk-hint">{{ item.hint }}</span>
        }
        <span class="cmdk-enter"><i class="ti ti-corner-down-left"></i> Abrir</span>
      </span>
    </button>
    @if (item.route) {
      <button
        type="button"
        class="cmdk-fav"
        tabindex="-1"
        [class.is-on]="isFavorite(item)"
        [attr.aria-pressed]="isFavorite(item)"
        [attr.aria-label]="favoriteLabel(item)"
        (click)="toggleFavorite(item, $event)"
      >
        <i class="ti" [class.ti-star-filled]="isFavorite(item)" [class.ti-star]="!isFavorite(item)"></i>
      </button>
    }
  </div>
</ng-template>
